import { useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ApiError } from "@dfp/api-client";
import { useAuth } from "../auth/AuthContext.js";
import { Button } from "../components/Button.js";
import { browserTokens } from "../lib/tokens.js";

export function LoginPage(): JSX.Element {
  const auth = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function onSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setPending(true);
    setError(null);
    try {
      const result = await auth.login({
        email,
        password,
        deviceFingerprint: browserTokens.getDeviceFingerprint(),
      });
      // Force-change gate: if the server flagged this login as
      // requiring a password change, the user's bearer token can ONLY
      // access POST /api/auth/me/password until they comply. Route
      // them to the change-password screen instead of the dashboard.
      void navigate({ to: result.mustChangePassword ? "/auth/change-password" : "/" });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.message ?? err.problem.detail ?? "Login failed");
      } else {
        console.error(err);
        setError("Network error — is the API running?");
      }
    } finally {
      setPending(false);
    }
  }

  return (
    <div className="min-h-full flex items-center justify-center bg-simuflex-deepNavy px-4 relative overflow-hidden select-none font-sans">
      {/* Decorative Brand Background Orbs */}
      <div className="absolute top-[-10%] left-[-10%] w-[45vw] h-[45vw] rounded-full bg-simuflex-green/10 blur-[120px] animate-float" />
      <div
        className="absolute bottom-[-10%] right-[-10%] w-[40vw] h-[40vw] rounded-full bg-blue-600/10 blur-[100px] animate-float"
        style={{ animationDelay: "3s" }}
      />

      <div className="w-full max-w-md rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-8 shadow-[0_24px_64px_-16px_rgba(9,15,30,0.5)] z-10 animate-fade-in">
        {/* Custom SVG SimuFlex Logo mark */}
        <div className="flex flex-col items-center mb-6">
          <svg
            className="w-16 h-16 mb-4 drop-shadow-[0_4px_12px_rgba(29,185,84,0.25)]"
            viewBox="0 0 100 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            {/* Phone Silhouette in white/navy outline */}
            <rect
              x="34"
              y="16"
              width="32"
              height="68"
              rx="6"
              fill="#122A4D"
              stroke="#ffffff"
              strokeWidth="4"
            />
            <rect x="46" y="20" width="8" height="2" rx="1" fill="#ffffff" opacity="0.3" />
            <circle cx="50" cy="76" r="3" fill="#ffffff" opacity="0.5" />
            {/* Swoosh "S" in Flex Green wrapping around */}
            <path
              d="M21 61C21 61 24 74 36 74C48 74 53 58 51 46C49 34 40 33 42 42C44 51 62 36 76 22C76 22 64 34 54 34C44 34 42 24 52 20C62 16 70 30 58 50C46 70 32 68 21 61Z"
              fill="#1DB954"
            />
          </svg>

          <h1 className="text-3xl font-extrabold tracking-tight text-white flex items-center">
            <span>Simu</span>
            <span className="text-simuflex-green">Flex</span>
          </h1>
          <p className="mt-1.5 text-xs font-semibold text-slate-400 tracking-widest uppercase">
            Phones. Finance. Freedom.
          </p>
        </div>

        <div className="border-t border-white/5 my-5" />

        <form onSubmit={onSubmit} className="space-y-4">
          <label className="block">
            <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
              Email address
            </span>
            <input
              type="email"
              autoComplete="email"
              required
              placeholder="admin@simuflex.local"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1.5 block w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-slate-500 shadow-inner outline-none focus:border-simuflex-green focus:ring-2 focus:ring-simuflex-green/20 focus:bg-white/[0.08] transition-all duration-200"
            />
          </label>

          <label className="block">
            <span className="text-[11px] font-bold text-slate-300 uppercase tracking-wider">
              Password
            </span>
            <input
              type="password"
              autoComplete="current-password"
              required
              minLength={12}
              placeholder="••••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1.5 block w-full rounded-xl border border-white/10 bg-white/[0.04] px-4 py-3 text-sm text-white placeholder-slate-500 shadow-inner outline-none focus:border-simuflex-green focus:ring-2 focus:ring-simuflex-green/20 focus:bg-white/[0.08] transition-all duration-200"
            />
          </label>

          {error ? (
            <div className="rounded-xl bg-simuflex-orange/10 border border-simuflex-orange/20 px-4 py-3 text-xs font-semibold text-simuflex-orange leading-normal animate-fade-in">
              {error}
            </div>
          ) : null}

          <Button
            type="submit"
            disabled={pending}
            variant="accent"
            className="w-full py-3.5 mt-2 text-sm font-bold tracking-wide"
          >
            {pending ? "Signing in…" : "Sign in to Console"}
          </Button>
        </form>

        {/* Dev-only bootstrap hint. import.meta.env.DEV is true under
            `vite` dev and false in any production build, so this internal
            tooling instruction never ships to a real operator's login. */}
        {import.meta.env.DEV ? (
          <p className="mt-6 text-center text-xs text-slate-500 font-medium">
            Bootstrap an admin account via:
            <code className="block mt-2 rounded-lg bg-white/[0.04] border border-white/5 py-1.5 px-2 font-mono text-[10px] text-simuflex-green select-all">
              pnpm --filter @dfp/api iam:bootstrap
            </code>
          </p>
        ) : null}
      </div>
    </div>
  );
}
